import React, { useState, useEffect, useCallback, useRef } from 'react';
import TreeNode from './TreeNode';
import CreateFolderForm from './CreateFolderForm';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Box, Dialog, DialogTitle, IconButton, DialogContent, Typography, DialogActions, Button, CircularProgress, Alert, Snackbar } from '@mui/material';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';



const TreeView = ({ 
  setActivePage, 
  selectedFolderId, 
  setSelectedFolderId,
  expandedFolders,
  setExpandedFolders
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [treeData, setTreeData] = useState({
  ownFolders: [],
  freeFolders: [],
  paidFolders: [],
});
const [userRole, setUserRole] = useState(sessionStorage.getItem("userRole"));

  const [selectedNodeType, setSelectedNodeType] = useState(null);
  const [isDuplicateAlertOpen, setIsDuplicateAlertOpen] = useState(false);
  const [duplicateErrorMessage, setDuplicateErrorMessage] = useState('');
  const [isDuplicateSubfolderAlertOpen, setIsDuplicateSubfolderAlertOpen] = useState(false);
  const [duplicateSubfolderErrorMessage, setDuplicateSubfolderErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
  const [error, setError] = useState(null);
  const isFetching = useRef(false);
  const [myPermissions, setMyPermissions] = useState({
    can_create_folder: false,
    can_create_sub: false,
    can_update_folder: false,
    can_delete_folder: false,
    can_read_folder: false,
    is_admin: false,
  });


  // Add this ref at the top of your TreeView component, after other refs
const scrollContainerRef = useRef(null);
const autoScrollInterval = useRef(null);

// Add this auto-scroll function
const handleDragOver = useCallback((e) => {
  const container = scrollContainerRef.current;
  if (!container) return;

  const rect = container.getBoundingClientRect();
  const scrollZone = 50; // pixels from edge to trigger scroll
  const scrollSpeed = 10; // pixels per scroll
  const mouseY = e.clientY;

  // Clear existing interval
  if (autoScrollInterval.current) {
    clearInterval(autoScrollInterval.current);
    autoScrollInterval.current = null;
  }

  // Scroll up when near top
  if (mouseY - rect.top < scrollZone) {
    autoScrollInterval.current = setInterval(() => {
      container.scrollTop -= scrollSpeed;
    }, 16); // ~60fps
  }
  // Scroll down when near bottom
  else if (rect.bottom - mouseY < scrollZone) {
    autoScrollInterval.current = setInterval(() => {
      container.scrollTop += scrollSpeed;
    }, 16);
  }
}, []);

// Add cleanup function
const handleDragEnd = useCallback(() => {
  if (autoScrollInterval.current) {
    clearInterval(autoScrollInterval.current);
    autoScrollInterval.current = null;
  }
}, []);

// Cleanup on unmount
useEffect(() => {
  return () => {
    if (autoScrollInterval.current) {
      clearInterval(autoScrollInterval.current);
    }
  };
}, []);

 useEffect(() => {
  const fetchMyPermissions = async () => {
    try {
      const token = sessionStorage.getItem("authToken");
      const response = await axios.get(`${apiUrl}/user/permissions/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const { can_create_folder, can_create_sub, can_update_folder, can_delete_folder, can_read_folder } = response.data.permissions || {};
      setMyPermissions({
        can_create_folder: can_create_folder || false,
        can_create_sub: can_create_sub || false,
        can_update_folder: can_update_folder || false,
        can_delete_folder: can_delete_folder || false,
        can_read_folder: can_read_folder || false,
        is_admin: userRole === "admin",
      });
    } catch (error) {
      console.error("Failed to fetch permissions:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch permissions. Please try again.",
        severity: "error",
      });
      setMyPermissions({
        can_create_folder: false,
        can_create_sub: false,
        can_update_folder: false,
        can_delete_folder: false,
        can_read_folder: false,
        is_admin: false,
      });
    }
  };

  const role = sessionStorage.getItem("userRole");
  setUserRole(role);
  if (role === "admin") {
    setMyPermissions({
      can_create_folder: true,
      can_create_sub: true,
      can_update_folder: true,
      can_delete_folder: true,
      can_read_folder: true,
      is_admin: true,
    });
  } else {
    fetchMyPermissions();
  }
}, []);


  const token = sessionStorage.getItem("authToken");
  const apiUrl = process.env.REACT_APP_BASE_URL;

  const getErrorMessage = (status, apiError = '') => {
    if (status === 400 && apiError) {
      return apiError;
    }
    if (status === 401) return "Unauthorized: Please log in again.";
    if (status === 403) return "Forbidden: You lack permission to perform this action.";
    if (status >= 500) return "Server error: Please try again later.";
    return "Failed to perform the action.";
  };

 const fetchTreeData = useCallback(async () => {
  if (!token) {
    setSnackbar({ open: true, message: "No authentication token found", severity: 'error' });
    return;
  }
  if (isFetching.current) return;
  isFetching.current = true;
  setIsLoading(true);
  setError(null);

  try {
    const response = await fetch(`${apiUrl}/folders/`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    console.log('fetchTreeData response:', { status: response.status, ok: response.ok });
    if (!response.ok) {
      let errorData = {};
      try {
        errorData = await response.json();
        console.log('fetchTreeData error response:', errorData);
      } catch (jsonError) {
        console.error('Error parsing fetchTreeData JSON:', jsonError);
      }
      const errorMessage = errorData.detail || errorData.error || "Failed to load folders.";
      if (errorData.detail === "You do not have permission to perform this action.") {
        setSnackbar({ open: true, message: "You don't have permission to view folders.", severity: 'error' });
      } else {
        setError(errorMessage);
      }
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const data = await response.json();

    // Pass the entire response object to formatTreeData
    setTreeData(formatTreeData(data));
  } catch (error) {
    console.error("Error fetching folders:", error);
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      setSnackbar({ open: true, message: "Network error: Unable to connect to the server.", severity: 'error' });
    } else {
      setError(error.message || "Failed to load folders.");
    }
  } finally {
    setIsLoading(false);
    isFetching.current = false;
  }
}, [token]);


  useEffect(() => {
    fetchTreeData();
  }, [fetchTreeData]);

const formatTreeData = (data) => {
  const ownFolders = data.own_folders || [];
  const freeFolders = data.assigned_folders?.free || [];
  const paidFolders = data.assigned_folders?.paid || [];

  // Helper to prevent circular references
  const processSubfolders = (subfolders, parentId, visited = new Set()) => {
    return subfolders
      .filter(sf => sf.id !== parentId && !visited.has(sf.id)) // Prevent self-reference
      .map(subfolder => {
        visited.add(subfolder.id);
        return {
          ...subfolder,
          type: "subfolder",
          children: subfolder.subfolders 
            ? processSubfolders(subfolder.subfolders, subfolder.id, new Set(visited))
            : [],
        };
      });
  };

  const filterTopLevel = (folders) => {
    return folders.filter(folder => folder.parent === null);
  };

  return {
    ownFolders: filterTopLevel(ownFolders).map(folder => ({
      ...folder,
      type: "folder",
      children: processSubfolders(folder.subfolders, folder.id),
    })),
    freeFolders: filterTopLevel(freeFolders).map(folder => ({
      ...folder,
      type: "folder",
      children: processSubfolders(folder.subfolders, folder.id),
    })),
    paidFolders: filterTopLevel(paidFolders).map(folder => ({
      ...folder,
      type: "folder",
      children: processSubfolders(folder.subfolders, folder.id),
    })),
  };
};


const findNode = (id, nodes) => {
  for (let node of nodes) {
    if (node.id === id) return node;
    if (node.children && node.children.length > 0) {
      const found = findNode(id, node.children);
      if (found) return found;
    }
  }
  return null;
};


  const moveNode = async (sourceId, destinationId) => {
  const allNodes = [...treeData.ownFolders, ...treeData.freeFolders, ...treeData.paidFolders];
  const sourceNode = findNode(sourceId, allNodes);
  const destinationNode = findNode(destinationId, allNodes);

  if (!sourceNode || !destinationNode) {
    setError("Invalid source or destination folder.");
    return;
  }

  // Prevent moving a node into itself
  if (sourceId === destinationId) {
    setError("Cannot move a folder into itself.");
    return;
  }

  // Rest of your existing validation...
  const isSourceOwn = findNode(sourceId, treeData.ownFolders) !== null;
  const isDestinationOwn = findNode(destinationId, treeData.ownFolders) !== null;
  
  if (isSourceOwn !== isDestinationOwn) {
    setError("Cannot move folders between different types.");
    return;
  }

  if (destinationNode.type === 'subfolder') {
    setError("Nothing can be dragged inside a subfolder.");
    return;
  }

    if (destinationNode.type === 'subfolder') {
      setError("Nothing can be dragged inside a subfolder.");
      return;
    }

    const apiUrl = process.env.REACT_APP_BASE_URL;
    const url =
      sourceNode.type === 'folder'
        ? `${apiUrl}/folders/${sourceId}/move_folder/`
        : `${apiUrl}/subfolders/${sourceId}/move_subfolder/`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "Cache-Control": "no-cache",
        },
        body: JSON.stringify({ new_parent_id: destinationId }),
      });
      console.log('moveNode response:', { status: response.status, ok: response.ok });
      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
          console.log('moveNode error response:', errorData);
        } catch (jsonError) {
          console.error('Error parsing moveNode JSON:', jsonError);
        }
        setError(getErrorMessage(response.status, errorData.error));
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      await fetchTreeData();
    } catch (error) {
      console.error("Error moving folder:", error);
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        setError("Network error: Unable to connect to the server.");
      } else {
        setError(error.message || "Failed to move folder.");
      }
    }
  };

  const handleCreateFolder = async ({ name, type }) => {
    const parentId = selectedFolderId;
    const apiUrl = process.env.REACT_APP_BASE_URL;

    const url =
      type === 'folder'
        ? `${apiUrl}/folders/`
        : `${apiUrl}/folders/${parentId}/create_subfolder/`;

    console.log('handleCreateFolder request:', { url, name, type, parentId, token: token ? 'Present' : 'Missing' });

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "Cache-Control": "no-cache",
        },
        body: JSON.stringify({ name }),
      });

      console.log('handleCreateFolder response:', {
        status: response.status,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries()),
      });

      if (!response.ok) {
        let errorData = {};
        let rawResponseText = '';

        try {
          rawResponseText = await response.text();
          console.log('handleCreateFolder raw response text:', rawResponseText);
          errorData = JSON.parse(rawResponseText);
          console.log('handleCreateFolder parsed error response:', errorData);
        } catch (jsonError) {
          console.error('Error parsing handleCreateFolder JSON:', jsonError, { rawResponseText });
          errorData = { error: rawResponseText || "Failed to create folder." };
        }

        if (response.status === 400 && errorData.error && errorData.error.toLowerCase().includes("already exists")) {
          console.log('Detected duplicate error:', errorData.error);
          if (type === 'folder') {
            setDuplicateErrorMessage(errorData.error);
            setIsDuplicateAlertOpen(true);
          } else {
            setDuplicateSubfolderErrorMessage(errorData.error);
            setIsDuplicateSubfolderAlertOpen(true);
          }
          return;
        }

        const errorMessage = getErrorMessage(response.status, errorData.error);
        console.log('Setting error message:', errorMessage);
        setError(errorMessage);
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      console.log('Folder created successfully, refreshing tree data');
      await fetchTreeData();
    } catch (error) {
      console.error('handleCreateFolder caught error:', {
        name: error.name,
        message: error.message,
        stack: error.stack,
      });

      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        setError("Network error: Unable to connect to the server.");
      } else if (!isDuplicateAlertOpen && !isDuplicateSubfolderAlertOpen) {
        const fallbackMessage = error.message || "Failed to create folder.";
        console.log('Setting fallback error message:', fallbackMessage);
        setError(fallbackMessage);
      }
    }
  };

  const handleSelect = (id, type) => {
  console.log("Selected:", id, type);
  setSelectedFolderId(id);
  setSelectedNodeType(type);
  
  // ✅ FIX: Update page title immediately when selecting any folder/subfolder
  const allFolders = [...treeData.ownFolders, ...treeData.freeFolders, ...treeData.paidFolders];
  const selectedNode = findNode(id, allFolders);
  
  if (selectedNode && window.updatePageTitle) {
    window.updatePageTitle(selectedNode);
  }
};

  const handleCloseDuplicateAlert = () => {
    setIsDuplicateAlertOpen(false);
    setDuplicateErrorMessage('');
  };

  const handleCloseDuplicateSubfolderAlert = () => {
    setIsDuplicateSubfolderAlertOpen(false);
    setDuplicateSubfolderErrorMessage('');
  };

  const handleCloseErrorAlert = () => {
    setError(null);
  };

 const filterTreeData = (nodes, query) => {
  if (!query) return nodes;

  return nodes
    .map((node) => {
      const nodeMatches = node.name.toLowerCase().includes(query.toLowerCase());
      const filteredChildren = filterTreeData(node.children, nodeMatches ? "" : query);

      if (nodeMatches || filteredChildren.length > 0) {
        return { ...node, children: filteredChildren };
      }
      return null;
    })
    .filter((node) => node !== null);
};


  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%',
        overflow: 'hidden'
      }}>
        {/* Fixed Section - Create Folder and Search */}
        <Box sx={{ 
          flexShrink: 0,
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          {isLoading && <CircularProgress size={24} sx={{ display: 'block', mx: 'auto', mb: 1 }} />}
          {error && (
            <Alert severity="error" onClose={handleCloseErrorAlert} sx={{ mb: 1 }}>
              {snackbar.message}
            </Alert>
          )}
          
          <CreateFolderForm 
            onCreateFolder={handleCreateFolder} 
            disableCreate={selectedNodeType === 'subfolder'} 
          />
          
          <Box sx={{ mt: 1, px: 0.5 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search folders..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "action.active" }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setSearchQuery("")}
                      size="small"
                      sx={{ color: "action.active" }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: {
                  fontSize: "0.9rem",
                  backgroundColor: (theme) => theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(0, 0, 0, 0.03)",
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: (theme) => theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.23)"
                      : "rgba(0, 0, 0, 0.23)",
                  },
                  "&:hover fieldset": {
                    borderColor: (theme) => theme.palette.primary.main,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: (theme) => theme.palette.primary.main,
                    borderWidth: "1px",
                  },
                  height: "40px",
                },
              }}
            />
          </Box>
        </Box>

        {/* Scrollable Folder List */}
  <Box 
  ref={scrollContainerRef}
  onDragOver={handleDragOver}
  onDragEnd={handleDragEnd}
  onDrop={handleDragEnd}
  sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', mt: 1, pr: 0.5 }}
>
  {/* Admin View: Only show "Admin's Folders" */}
  {userRole === "admin" ? (
    <>
      <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
        Admin's Folders
      </Typography>
      {filterTreeData(treeData.ownFolders, searchQuery).length > 0 ? (
        filterTreeData(treeData.ownFolders, searchQuery).map((node) => (
          <TreeNode
            key={node.id}
            node={node}
            moveNode={moveNode}
            onSelect={handleSelect}
            selectedFolderId={selectedFolderId}
            fetchTreeData={fetchTreeData}
            myPermissions={myPermissions}
            setActivePage={setActivePage}
            expandedFolders={expandedFolders}
            setExpandedFolders={setExpandedFolders}
          />
        ))
      ) : (
        <Box sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>
          No Admin Folders Available!
        </Box>
      )}
    </>
  ) : (
    // Student View: Show all sections
    <>
      {/* Own Folders Section */}
      <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
        My Folders
      </Typography>
      {filterTreeData(treeData.ownFolders, searchQuery).length > 0 ? (
        filterTreeData(treeData.ownFolders, searchQuery).map((node) => (
          <TreeNode
            key={node.id}
            node={node}
            moveNode={moveNode}
            onSelect={handleSelect}
            selectedFolderId={selectedFolderId}
            fetchTreeData={fetchTreeData}
            myPermissions={myPermissions}
            setActivePage={setActivePage}
            expandedFolders={expandedFolders}
            setExpandedFolders={setExpandedFolders}
          />
        ))
      ) : (
        <Box sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>
          No Own Folders Available!
        </Box>
      )}

      {/* Free Folders Section */}
      <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
        Free Folders
      </Typography>
      {filterTreeData(treeData.freeFolders, searchQuery).length > 0 ? (
        filterTreeData(treeData.freeFolders, searchQuery).map((node) => (
          <TreeNode
            key={node.id}
            node={node}
            moveNode={moveNode}
            onSelect={handleSelect}
            selectedFolderId={selectedFolderId}
            fetchTreeData={fetchTreeData}
            myPermissions={myPermissions}
            setActivePage={setActivePage}
            expandedFolders={expandedFolders}
            setExpandedFolders={setExpandedFolders}
          />
        ))
      ) : (
        <Box sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>
          No Free Folders Available!
        </Box>
      )}

      {/* Paid Folders Section */}
      <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
        Paid Folders 👑
      </Typography>
      {filterTreeData(treeData.paidFolders, searchQuery).length > 0 ? (
        filterTreeData(treeData.paidFolders, searchQuery).map((node) => (
          <TreeNode
            key={node.id}
            node={node}
            moveNode={moveNode}
            onSelect={handleSelect}
            selectedFolderId={selectedFolderId}
            fetchTreeData={fetchTreeData}
            myPermissions={myPermissions}
            setActivePage={setActivePage}
            expandedFolders={expandedFolders}
            setExpandedFolders={setExpandedFolders}
          />
        ))
      ) : (
        <Box sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>
          No Paid Folders Available!
        </Box>
      )}
    </>
  )}
</Box>


      </Box>

      {/* Dialog for Duplicate Folder */}
      <Dialog open={isDuplicateAlertOpen} onClose={handleCloseDuplicateAlert}>
        <DialogTitle>Duplicate Folder</DialogTitle>
        <DialogContent>
          <p>{duplicateErrorMessage || "A folder with this name already exists."}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDuplicateAlert} color="primary" variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for Duplicate Subfolder */}
      <Dialog open={isDuplicateSubfolderAlertOpen} onClose={handleCloseDuplicateSubfolderAlert}>
        <DialogTitle>Duplicate Subfolder</DialogTitle>
        <DialogContent>
          <p>{duplicateSubfolderErrorMessage || "A subfolder with this name already exists under the parent folder."}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDuplicateSubfolderAlert} color="primary" variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </DndProvider>
  );
};

export default TreeView;